// DirtyBadge — the single visible indicator of unsaved or out-of-sync state.
// Used everywhere a resource can drift from the server: character header,
// blueprint tab titles, texture slots, prefab editor.
//
// States (UX consistency rule — same pattern everywhere):
//   clean         → no badge rendered (the explicit absence IS the signal).
//   dirty         → orange dot, tooltip "Unsaved changes".
//   saving        → small spinner.
//   error         → red ! with hover-error message.
//   stale-backend → yellow ⚠ — the local draft was authored against a
//                   different backend than the one currently connected; it
//                   will not auto-apply.
//
// Pure visual. State management lives in the caller.

import React from 'react'

// Status colors come from the palette token contract (ENG-880) so the badge
// follows the selected theme like every other surface.
const COLORS = {
  dirty: 'var(--m-warn)',
  saving: 'var(--m-accent)',
  error: 'var(--m-danger)',
  staleBackend: 'var(--m-warn)',
}

const TOOLTIPS = {
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Save failed',
  'stale-backend': 'Draft from a different backend — won\'t auto-apply',
}

export default function DirtyBadge({ state = 'clean', message, size = 8, style }) {
  if (state === 'clean') return null

  const title = message ?? TOOLTIPS[state] ?? ''
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size + 2,
    height: size + 2,
    flexShrink: 0,
    ...style,
  }

  if (state === 'saving') {
    return (
      <span
        role="status"
        title={title}
        aria-label={title}
        style={base}
      >
        <span
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: `1.5px solid color-mix(in srgb, ${COLORS.saving} 20%, transparent)`,
            borderTopColor: COLORS.saving,
            animation: 'dirtybadge-spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes dirtybadge-spin { to { transform: rotate(360deg) } }`}</style>
      </span>
    )
  }

  if (state === 'error') {
    return (
      <span
        role="img"
        title={title}
        aria-label={title}
        style={{
          ...base,
          color: COLORS.error,
          fontFamily: 'var(--m-font)',
          fontWeight: 700,
          fontSize: size + 2,
          lineHeight: 1,
        }}
      >
        !
      </span>
    )
  }

  if (state === 'stale-backend') {
    return (
      <span
        role="img"
        title={title}
        aria-label={title}
        style={{
          ...base,
          color: COLORS.staleBackend,
          fontSize: size + 4,
          lineHeight: 1,
        }}
      >
        ⚠
      </span>
    )
  }

  // dirty (default visible state)
  return (
    <span
      role="img"
      title={title}
      aria-label={title}
      style={base}
    >
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: COLORS.dirty,
          boxShadow: `0 0 4px color-mix(in srgb, ${COLORS.dirty} 60%, transparent)`,
        }}
      />
    </span>
  )
}
